import moment from 'moment';
import ReactQuill from 'react-quill';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import AppBar from '@material-ui/core/AppBar';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import 'react-accessible-accordion/dist/fancy-example.css';
import { TextField, CircularProgress } from '@mui/material';

import { getIntegrationStatus } from '../../store/Integration';
import CreateRelation from '../../components/Activities/prop/CreateRelation';
import ConnectionStatus from '../../components/Integration/ConnectionStatus';
import IntegrationSelector from '../../components/Integration/IntegrationSelector';
import { getCommunicationHistory, sendCommunication } from '../../store/Activities';

const RightSidebar = ({ setIsRightSidebar, singlePageData, objectType }) => {
  const dispatch = useDispatch();

  const chatBoxRef = useRef(null);
  let { currentWorkspace } = useSelector(state => state['Settings']);

  // Get SMS state from Redux (messages/sending from Activities, connection from Integration)
  const { associations } = useSelector(state => state['Activities']);
  const integrationStatus = useSelector(state => state['Integration']['integrationStatus']);
  const { messages, loading, sending } = useSelector(state => state['Activities']['communication']['sms']);

  // Always use lowercase key for consistency (saga normalizes all keys to lowercase)
  const twilioStatus = integrationStatus?.['twilio'];
  const isCheckingConnection = twilioStatus?.['loading'] ?? true;
  const hasConnection = twilioStatus?.['data']?.['isConnected'] ?? false;

  // Local state for form inputs
  let [selectedTab, setSelectedTab] = useState(0);
  let [messageText, setMessageText] = useState('');
  let [showMoreDropdown, setShowMoreDropdown] = useState(false);
  let [selectedIntegrationId, setSelectedIntegrationId] = useState('');

  // ReactQuill editor config
  const editorModules = { toolbar: '#chat-toolbar', clipboard: { matchVisual: false } };
  const formats = ['font', 'size', 'color', 'background', 'align', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'link', 'image', 'align'];

  // Toggle dropdown
  const toggleDropdown = () => setShowMoreDropdown(prev => !prev);

  // Character count
  const messageLength = messageText.replace(/<[^>]*>/g, '').length; // Strip HTML tags for count
  const remainingChars = 160 - (messageLength % 160);
  const segments = Math.ceil(messageLength / 160) || 1;

  // Get contact info from singlePageData
  const contactId = singlePageData?.['_id'] || singlePageData?.['contacts']?.[0]?.['_id'] || '';
  const contactPhone = singlePageData?.['phone'] || singlePageData?.['contacts']?.[0]?.['phone'] || '';
  const contactEmail = singlePageData?.['email'] || singlePageData?.['contacts']?.[0]?.['email'] || '';
  const contactName = singlePageData?.['firstName'] ? `${singlePageData['firstName']} ${singlePageData['lastName'] || ''}`.trim()
                    : singlePageData?.contacts?.[0]?.['firstName'] ? `${singlePageData['contacts'][0]['firstName']} ${singlePageData['contacts'][0]['lastName'] || ''}`.trim() : 'Contact';

  // Check Twilio connection on mount (use exact tool name from database)
  useEffect(() => { dispatch(getIntegrationStatus('Twilio')) }, [dispatch]);

  // Fetch messages when connected and has contactPhone
  useEffect(() => {
    if (hasConnection && contactPhone) dispatch(getCommunicationHistory({ channel: 'sms', identifier: contactPhone }));
  }, [hasConnection, contactPhone, dispatch]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatBoxRef['current']) chatBoxRef['current']['scrollTop'] = chatBoxRef['current']['scrollHeight'];
  }, [messages]);

  // Close sidebar on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsRightSidebar(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setIsRightSidebar]);

  const handleSendSMS = () => {
    if (!messageText.trim() || !contactPhone) return;

    // Build associations object from Redux state, fallback to contactId from props
    const smsAssociations = { ...associations };
    if (!smsAssociations['contacts']?.length && contactId) smsAssociations['contacts'] = [contactId];

    dispatch(sendCommunication({
      channel: 'sms',
      to: contactPhone,
      associations: smsAssociations,
      integrationId: selectedIntegrationId || undefined,
      message: messageText.replace(/<[^>]*>/g, '').trim(),
      successCallback: () => {
        setMessageText('');
        dispatch(getCommunicationHistory({ channel: 'sms', identifier: contactPhone }));
      }
    }));
  };

  const handleKeyPress = (e) => {
    // Send SMS on Ctrl+Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendSMS();
    }
  };

  const formatTime = (timestamp) => { return moment(timestamp).format('h:mm a') }

  const formatDate = (timestamp) => {
    const date = moment(timestamp);
    if (date.isSame(moment(), 'day')) return 'Today';
    if (date.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
    return date.format('MMMM Do, YYYY');
  };

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const date = moment(msg.timestamp || msg.createdAt).format('YYYY-MM-DD');
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const groupedMessages = groupMessagesByDate(messages);

  // Loading state while checking connection
  if (isCheckingConnection) {
    return (
      <aside className="deal-sidebar style-right-sidebar">
        <div className='top-massage-area'>
          <div className='header-chatbox'>
            <div className='left-title-area'>
              <h3>SMS Chat</h3>
            </div>
            <div className='right-area' style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
              <button className='massage-box close-btn' onClick={() => setIsRightSidebar(false)} title="Close">
                <i className='icon'>×</i>
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', flexDirection: 'column' }}>
            <CircularProgress size={24} />
            <span style={{ marginTop: '10px', fontSize: '13px' }}>Checking SMS connection...</span>
          </div>
        </div>
      </aside>
    );
  }

  // Not connected - show connect UI
  if (!hasConnection) {
    return (
      <aside className="deal-sidebar style-right-sidebar">
        <div className='top-massage-area'>
          <div className='header-chatbox'>
            <div className='left-title-area'>
              <h3>SMS Chat</h3>
            </div>
            <div className='right-area' style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
              <button className='massage-box close-btn' onClick={() => setIsRightSidebar(false)} title="Close">
                <i className='icon'>×</i>
              </button>
            </div>
          </div>
          <div style={{ padding: '20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="#4B5563" />
                <path d="M7 7H17V9H7V7ZM7 11H17V13H7V11Z" fill="#4B5563" />
              </svg>
              <h4 style={{ marginTop: '10px', fontSize: '14px' }}>Connect Twilio to Send SMS</h4>
              <p style={{ color: '#89868D', fontSize: '13px' }}>Connect Twilio to send SMS messages directly from your CRM.</p>
            </div>
            <ConnectionStatus
              toolName="Twilio"
              compact={true}
              onConnect={() => dispatch(getIntegrationStatus('Twilio'))}
            />
          </div>
        </div>
      </aside>
    );
  }

  // Connected - show chat UI
  return (
    <aside className="deal-sidebar style-right-sidebar">
      <div className='top-massage-area'>
        <div className='header-chatbox'>
          <div className='left-title-area'> <h3>SMS Chat</h3> </div>
          <div className='right-area' style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
            <button className='massage-box' onClick={() => dispatch(getCommunicationHistory({ channel: 'sms', identifier: contactPhone }))} title="Refresh messages">
              <i className='icon'>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3019 3 18.1885 4.77814 19.7545 7.42909M21 3V8H16" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </i>
            </button>
            <button className='massage-box close-btn' onClick={() => setIsRightSidebar(false)} title="Close"> <i className='icon'>×</i> </button>
          </div>
        </div>
        <div className='user-area' style={{ padding: '8px 0', borderBottom: '0.5px solid var(--sa-dark-border)', marginBottom: '8px' }}>
          <div className='left-area'>
            <div className='img-box'>
              <img src={require('../../static/img/user-img-1.png')} alt='' />
            </div>
            <div>
              <p style={{ marginBottom: '0', fontSize: '13px' }}>{contactName}</p>
              <small style={{ color: '#89868D', fontSize: '11px', display: 'block' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                {contactPhone}
              </small>
              {contactEmail && <small style={{ color: '#89868D', fontSize: '11px', display: 'block' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                {contactEmail}
              </small>}
            </div>
          </div>
        </div>
        <div className='chat-box-area' ref={chatBoxRef}>
          {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}> <CircularProgress size={30} /> </div>
          : messages.length === 0 ? <div style={{ textAlign: 'center', padding: '40px 20px', color: '#89868D' }}>  <p>No messages yet. Start the conversation!</p> </div>
          : (Object.keys(groupedMessages).map(date => (
              <div key={date}>
                <div className='date-chat'> <h5>{formatDate(date)}</h5> </div>
                <div className='chat-box-inner'>
                  {groupedMessages[date].map((msg, index) => (
                    <div key={msg['_id'] || index} className={`chat-box ${msg['direction'] === 'inbound' ? 'assistant' : 'user'}`}>
                      <div className='chat-box-text'> {msg['message'] || msg['content'] || msg['body']} </div>
                      <span className='chat-time'>
                        {formatTime(msg['timestamp'] || msg['createdAt'])}
                        {msg['from'] && msg['direction'] === 'outbound' && <span className='chat-from'> · From: {msg['from']}</span>}
                        {msg['to'] && msg['direction'] === 'inbound' && <span className='chat-from'> · To: {msg['to']}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div className='bottom-massage-box-area'>
        <div className='tabs-area'>
          <div className='left-area'>
            <AppBar position="static">
              <Tabs value={selectedTab} onChange={(event, newValue) => setSelectedTab(newValue)} aria-label="simple tabs example">
                <Tab label="SMS" />
                <Tab label="Email" disabled />
              </Tabs>
            </AppBar>
          </div>
        </div>
        <div className='tabs-content'>
          <TabPanel value={selectedTab} index={0}>
            <div className='from-massage'>
              <div className='top-from-to'>
                <div className="chat-container">
                  <div className="chat-row">
                    <div className="chat-input-group" style={{ flex: 1 }}>
                      <label className="chat-label">From:</label>
                      <IntegrationSelector
                        toolName="Twilio"
                        value={selectedIntegrationId}
                        onChange={setSelectedIntegrationId}
                        placeholder="Select Twilio account"
                      />
                    </div>
                  </div>
                  <div className="chat-row" style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="chat-input-group" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <label className="chat-label">To:</label>
                      <input value={contactPhone} readOnly className="chat-to-input" />
                    </div>
                    {messageText.replace(/<[^>]*>/g, '').trim() && (
                      <button
                        onClick={handleSendSMS}
                        className='btn-style-send'
                        style={{ marginLeft: '10px' }}
                        disabled={sending || !contactPhone}
                      >
                        {sending ? <CircularProgress size={14} color="inherit" /> : 'Send'}
                      </button>
                    )}
                  </div>

                  <div className="editor-wrapper" style={{ marginTop: '10px' }} onKeyDown={handleKeyPress}>
                    <ReactQuill
                      theme="snow"
                      formats={formats}
                      readOnly={sending}
                      value={messageText}
                      modules={editorModules}
                      bounds=".editor-wrapper"
                      placeholder="Type your message... (Ctrl+Enter to send)"
                      onChange={(value) => {
                        const plainText = value.replace(/<[^>]*>/g, '');
                        if (plainText.length <= 1600) setMessageText(value);
                      }}
                    />

                    {/* Custom Toolbar - Same as SendSMS */}
                    <div id="chat-toolbar" className="bottom-toolbar" style={{ margin: '5px 10px 0' }}>
                      <span className="ql-formats">
                        <button className="ql-bold" />
                        <button className="ql-italic" />
                        <button className="ql-underline" />
                        <button className="ql-strike" />
                      </span>
                      <div className="ql-formats more-dropdown">
                        <button className="more-toggle" onClick={toggleDropdown}>More ▾</button>
                        <span
                          className="more-dropdown-content"
                          onClick={(e) => e.stopPropagation()}
                          style={{ display: showMoreDropdown ? 'block' : 'none' }}
                        >
                          <svg data-test-id="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28" style={{ position: 'absolute', pointerEvents: 'none', top: '100%', left: '7.7185px' }}><path clipPath="url(#:ra5:)" fill="none" stroke="#CBD6E2" strokeWidth="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                          <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                            <select className="ql-font" />
                            <select className="ql-size" />
                            <select className="ql-color" />
                            <select className="ql-background" />
                            <button className="ql-blockquote" />
                            <button className="ql-list" value="ordered" />
                            <select className="ql-align" />
                          </span>
                        </span>
                      </div>
                      <span className="ql-formats">
                        <button className="ql-list" value="ordered" />
                        <button className="ql-list" value="bullet" />
                      </span>
                      <span className="ql-formats">
                        <button className="ql-link" />
                        <button className="ql-image" />
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', fontSize: '11px', color: '#666', padding: '10px' }}>
                      <span>{messageLength}/1600 characters</span>
                      <span>{segments} segment{segments !== 1 ? 's' : ''} ({remainingChars} chars left)</span>
                    </div>
                  </div>
                  {/* Related with objects */}
                  <div className="add-note-sec inline-relation">
                    <div className="add-note-box">
                      <div className="bottom-note-box">
                        <CreateRelation
                          type="new"
                          initialAssociations={associations}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            <div className='from-massage'>
              <div className='top-from-to'>
                <div className="chat-container">
                  <div className='whatsapp-box'>
                    <div className='left-area'>
                      <h4>Reach out to your customer on their favourite messaging app</h4>
                      <p>Stay closer to your customers by providing instant support, sending timely updates, and creating engaging interactions.</p>
                    </div>
                    <div className='right-area'>
                      <button className='whatssapp-btn'>
                        <i><img src={require('../../static/img/whatssapp-icon.png')} alt='' /></i>
                        Connect WhatsApp
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            <div className='from-massage'>
              <div className='top-from-to'>
                <div className="chat-container">
                  <p style={{ textAlign: 'center', padding: '20px', color: '#89868D' }}>
                    Email functionality coming soon
                  </p>
                </div>
              </div>
            </div>
          </TabPanel>
        </div>
      </div>
    </aside>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other} >
      {value === index && <Box p={0}> <Typography component="div">{children}</Typography> </Box>}
    </div>
  )
}

export default RightSidebar;
